/**
 * The query string for `GET compliance/findings/`.
 *
 * Built from an explicit WHITELIST rather than by forwarding the page's search
 * params wholesale, because three of the contract's rules are only enforceable
 * at this boundary:
 *
 *  - `framework_control` without `framework` is a **400, not an empty list** —
 *    a bare control id is ambiguous, since the same number means different
 *    controls in CSCv7 and CSCv8. The UI gates the control field on the
 *    framework selector; this drops the pair defensively if it ever gets past.
 *  - `page_size` is capped at **10**.
 *  - Sorting is not a query param yet (the list sorts by `last_seen`
 *    descending, server-side), so an `ordering` left in the URL by another page
 *    must never reach this endpoint.
 */

/** Params the endpoint accepts as a comma-separated list of values. */
export const MULTI_VALUE_PARAMS = ['status', 'exception_type'] as const

/** Params the endpoint accepts as a single value. */
export const SINGLE_VALUE_PARAMS = [
  'title',
  'control_id',
  'benchmark_name',
  'benchmark_version',
  'profile',
  'component',
  'asset',
  'scan_id',
  'framework',
  'framework_control',
] as const

export type ComplianceFilterKey =
  | (typeof MULTI_VALUE_PARAMS)[number]
  | (typeof SINGLE_VALUE_PARAMS)[number]

/** Every filter key the findings list understands, in form order. */
export const FILTER_KEYS: readonly ComplianceFilterKey[] = [
  ...MULTI_VALUE_PARAMS,
  ...SINGLE_VALUE_PARAMS,
]

/** The backend's hard ceiling on `page_size`. */
export const MAX_PAGE_SIZE = 10

export type ComplianceFilterState = Partial<Record<ComplianceFilterKey, string[]>>

/**
 * Keep only the keys the endpoint knows, dropping blanks.
 *
 * Also the gate: `framework_control` survives only alongside a `framework`.
 */
export function normalizeFilters(raw: Record<string, string[] | undefined>): ComplianceFilterState {
  const out: ComplianceFilterState = {}

  for (const key of FILTER_KEYS) {
    const values = (raw[key] ?? []).map((value) => value.trim()).filter(Boolean)
    if (values.length === 0) continue
    // A single-value param takes the first value and ignores the rest; sending
    // "L1 Server,L2 Server" to an exact-match field matches nothing at all.
    out[key] = (MULTI_VALUE_PARAMS as readonly string[]).includes(key) ? values : [values[0]]
  }

  if (!out.framework?.length) delete out.framework_control

  return out
}

export interface BuildParamsInput {
  filters: Record<string, string[] | undefined>
  page?: number
  pageSize?: number
}

/**
 * The request query string. Returns `''` when there is nothing to send, so the
 * caller can hit the bare collection URL.
 */
export function buildFindingsParams({ filters, page, pageSize }: BuildParamsInput): string {
  const search = new URLSearchParams()
  const normalized = normalizeFilters(filters)

  for (const key of FILTER_KEYS) {
    const values = normalized[key]
    if (values?.length) search.set(key, values.join(','))
  }

  if (page != null && page > 1) search.set('page', String(page))
  if (pageSize != null) {
    search.set('page_size', String(Math.max(1, Math.min(MAX_PAGE_SIZE, Math.trunc(pageSize)))))
  }

  return search.toString()
}

/** How many filters are actually narrowing the list — drives the "Clear all" affordance. */
export function activeFilterCount(filters: Record<string, string[] | undefined>): number {
  return Object.keys(normalizeFilters(filters)).length
}

/**
 * A malformed ULID in `asset` or `scan_id` returns an empty result set rather
 * than an error, so a mistyped deep link looks exactly like "nothing matched".
 * The list says which id it is scoped to so that case is legible.
 */
export const ULID_PARAMS: readonly ComplianceFilterKey[] = ['asset', 'scan_id']
